import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id?: string
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'dark' | 'light'
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    eyebrow,
    title,
    description,
    align = 'left',
    tone = 'dark',
    className
}): ReactNode => {
    const isLight = tone === 'light'
    const isCentered = align === 'center'

    return (
        <div className={cn('max-w-3xl', isCentered && 'mx-auto text-center', className)}>
            <p
                className={cn(
                    'inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em]',
                    isLight ? 'text-gold' : 'text-bronze'
                )}
            >
                <span aria-hidden='true' className='h-px w-8 bg-current' />
                {eyebrow}
            </p>

            <h2
                id={id}
                className={cn(
                    'mt-5 text-balance font-heading text-[2.6rem] leading-[1.02] tracking-tight sm:text-5xl lg:text-[3.6rem]',
                    isLight ? 'text-primary-foreground' : 'text-foreground'
                )}
            >
                {title}
            </h2>

            {description && (
                <p
                    className={cn(
                        'mt-6 text-pretty text-lg leading-relaxed',
                        isCentered && 'mx-auto max-w-2xl',
                        isLight ? 'text-primary-foreground/75' : 'text-muted-foreground'
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    )
}
